import { Inject, Injectable } from '@angular/core';
import { DraftEmail, DraftEmailUpdate, ReceivedEmail, SentEmail, TemplateEmail, TemplateEmailInsert, TemplateEmailUpdate } from "../models/email-message";
import { Observable, catchError, distinctUntilChanged, filter, lastValueFrom, map, of, switchMap } from "rxjs";
import { Store } from '@ngxs/store';
import { ApiService } from './api.service';
import { UserService } from './user.service';
import { MessageService } from 'primeng/api';
import { SignalingService } from './hub.service';
import { plainToClass, plainToInstance } from 'class-transformer';
import { ErrorMessage } from '../models/errors';
import { HubConnection } from '@microsoft/signalr';
import { UserState } from '../state/user/user-state';
import { Emails } from '../state/email/email-action';
import { BASE_URL, EMAIL_HUB_URL, EMAIL_TEMPLATE_HUB_URL } from './injection-tokens';
import { TemplateEmails } from '../state/email/template-email-action';
import { User } from '../models/user';


export abstract class EmailServiceBase
{
    abstract getTemplateEmails(): Observable<TemplateEmail[]>;
    abstract getTemplateEmail(emailId: number): Observable<TemplateEmail>;
    abstract checkTemplateName(templateName: string): Observable<boolean>;
    abstract addTemplateEmail(email: TemplateEmailInsert): Promise<TemplateEmail | undefined>;
    abstract saveTemplateEmail(emailId: number, email: TemplateEmailUpdate): Promise<TemplateEmail | undefined>;
    abstract deleteTemplateEmail(emailId: number): Promise<TemplateEmail | undefined>;
    abstract readReceivedEmail(emailId: number): Promise<ReceivedEmail | undefined>;
    abstract applyTemplateEmail(templateId: number, referenceObject?: any): Promise<DraftEmail | undefined>

    abstract updateDraftEmail(emailId: number, email: DraftEmailUpdate): Promise<DraftEmail | undefined>;
    abstract deleteDraftEmail(emailId: number): Promise<DraftEmail | undefined>;


}  

@Injectable({providedIn: 'root'})
export class EmailService extends EmailServiceBase
{ 
    
    currentUser$ = this._store.select(UserState.currentUser);
    private _emailHub: HubConnection;
    private _templateHub: HubConnection;
    
    constructor
        (
            private _store: Store,
            private _api: ApiService,
            private _user: UserService,
            private _messages: MessageService,
            private _signaling: SignalingService,
            @Inject(EMAIL_TEMPLATE_HUB_URL) private _emailTemplateHubUrl: string,
            @Inject(EMAIL_HUB_URL) private _emailHubUrl: string,
            @Inject(BASE_URL) private _baseUrl: string,
        ) {
        super();

        this._emailHub = this._signaling.generateHub(_emailHubUrl);
        this._templateHub = this._signaling.generateHub(_emailTemplateHubUrl);

        this.currentUser$
            .pipe(
                filter((cu): cu is User => cu != null && cu.id != null),
                distinctUntilChanged((a, b) => a.id === b.id)
            )
            .subscribe
            (
                cu => {
                    if (cu?.id != null)
                    {
                        //Try starting the hub, once we're logged in
                        this._signaling.startHub(this._emailHub);
                        this._signaling.startHub(this._templateHub);
                    }
                    else
                    {
                        this._emailHub.stop().catch(err => console.error('Hub stop failed', err));
                        this._templateHub.stop().catch(err => console.error('Hub stop failed', err));
                    }
                }
            )
        this._emailHub.on("EmailDelivered", (email: SentEmail) => _store.dispatch(new Emails.EmailServiceEmailUpdated({ email: plainToInstance(SentEmail, email) })));
        this._emailHub.on("EmailNotDelivered", (email: SentEmail) => _store.dispatch(new Emails.EmailServiceEmailUpdated({ email: plainToInstance(SentEmail, email) })));
        this._emailHub.on("EmailRead", (email: SentEmail) => _store.dispatch(new Emails.EmailServiceEmailUpdated({ email: plainToInstance(SentEmail, email) })));
        this._emailHub.on("EmailSent", (email: SentEmail) => _store.dispatch(new Emails.EmailServiceEmailUpdated({ email: plainToInstance(SentEmail, email) })));
        this._templateHub.on("EmailTemplateAdded", (email: TemplateEmail) => _store.dispatch(new TemplateEmails.EmailServiceEmailTemplateAdded({ templateEmail: email })));
        this._templateHub.on("EmailTemplateSaved", (email: TemplateEmail) => _store.dispatch(new TemplateEmails.EmailServiceEmailTemplateSaved({ templateEmail: email })));
        this._emailHub.on("EmailDraftUpdated", (draftEmail: DraftEmail) => _store.dispatch(new Emails.EmailServiceEmailDraftUpdated({ draftEmail: plainToInstance(DraftEmail, draftEmail) })));
    }

  override getTemplateEmail(emailId: number): Observable<TemplateEmail> {
        return this._api.makeAPIGetCall<DraftEmail>(this._baseUrl, "/email/templates/" + emailId.toString(), {})
        .pipe
        (
            map
            (
                draft =>
                {
                    return plainToClass(TemplateEmail, draft);
                }
            )
        );
    }

    override getTemplateEmails(): Observable<TemplateEmail[]> {
        return this._api.makeAPIGetCall<TemplateEmail[]>(this._baseUrl, "/email/templates", {})
        .pipe
        (
            map
            (
                drafts => drafts.map(draft => plainToClass(TemplateEmail, draft))
            )
        );
    }

    override checkTemplateName(templateName: string): Observable<boolean> {
        return this._api.makeAPIGetCall<boolean>(this._baseUrl, "/email/templates/names/" + templateName, {});
    }

    override addTemplateEmail(email: TemplateEmailInsert): Promise<TemplateEmail | undefined> {
        return lastValueFrom(
            this._api.makeAPIPutCall<TemplateEmail>(this._baseUrl,"/email/templates", email)
            .pipe
            (
            //Ensure the store update completes before the promise resolves
            switchMap(e => this._store.dispatch(new TemplateEmails.EmailServiceEmailTemplateAdded({ templateEmail: e })).pipe(
                map(() => e)
            )),
            map(e => {
                this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Added Template Email' });
                return plainToClass(TemplateEmail, e);
            }),
                catchError((error:ErrorMessage) => {this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Create Template Email', detail: ' ' + error.message }); return of(undefined)})
            )
        )
    }

    override saveTemplateEmail(emailId: number, email: TemplateEmailUpdate): Promise<TemplateEmail | undefined> {
        return lastValueFrom(
            this._api.makeAPIPatchCall<TemplateEmail>(this._baseUrl, "/email/templates/" + emailId.toString(), email)
            .pipe(
                map
                (
                    e =>
                    {
                        this._store.dispatch(new TemplateEmails.EmailServiceEmailTemplateSaved({templateEmail:e}));

                        this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Updated Template Email'});
                        return plainToClass(TemplateEmail, e);
                    }
                ),
                catchError((error:ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'errror', summary: 'Could Not Update Template Email', detail: ' ' + error.message }); return of(undefined)})
            )
        )
    }

    override deleteTemplateEmail(emailId: number): Promise<TemplateEmail | undefined> {
        return lastValueFrom
        (
            this._api.makeAPIDeleteCall<TemplateEmail>(this._baseUrl,"/email/templates/"+ emailId.toString(),{})
                
            .pipe
            (
                map
                (
                    e =>
                    {
                        this._store.dispatch(new TemplateEmails.EmailServiceEmailTemplateDeleted({templateEmailId: emailId}));
                        this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Email Templated Deleted'});

                        return plainToInstance(TemplateEmail, e);
                    }
                ),
                catchError((error:ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'errror', summary: 'Could Not Delete Template Email', detail: ' ' + error.message }); return of(undefined)})
            )
        )
    }

    override readReceivedEmail(emailId: number): Promise<ReceivedEmail | undefined> {
        return lastValueFrom
        (
            this._api.makeAPIPatchCall<ReceivedEmail>(this._baseUrl, "/email/" + emailId.toString() + "/read", {})
            .pipe
            (
                map
                (
                    e =>
                    { 
                        this._store.dispatch(new Emails.EmailServiceEmailReceivedRead({emailId})); 
                        this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Read Received Email'});
                        return plainToClass(ReceivedEmail, e);
                    }
                ),
                catchError((error: any) => {this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Mark Email As Read', detail: error?.message ?? error?.title ?? '' }); return of(undefined)})
            )
        )
    }

    override applyTemplateEmail(templateId: number, referenceObject?: any): Promise<DraftEmail | undefined> {
        return lastValueFrom
        (
            this._api.makeAPIPostCall<DraftEmail>(this._baseUrl, "/email/templates/" + templateId.toString() + "/apply", referenceObject)
            .pipe
            (
                map
                (
                    e =>
                    {  
                        this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Template Applied!'});
                        return plainToClass(DraftEmail, e);
                    }
                ),
                catchError((error:ErrorMessage) => {this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Create Apply Template Email', detail: ' ' + error.message }); return of(undefined)})
            )
        )
    }

    override updateDraftEmail(emailId: number, email: DraftEmailUpdate): Promise<DraftEmail | undefined> {
        return lastValueFrom(
            this._api.makeAPIPatchCall<DraftEmail>(this._baseUrl, "/email/drafts/" + emailId.toString(), email)
            .pipe(
                map
                (
                    e =>
                    {
                        this._store.dispatch(new Emails.EmailServiceEmailDraftUpdated({draftEmail: e}));
                        this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Draft Email Updated'});
                        return plainToClass(DraftEmail, e);
                    }
                ),
                catchError((error:ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'errror', summary: 'Could Not Update Draft Email', detail: ' ' + error.message }); return of(undefined)})
            )
        )
    }

    override deleteDraftEmail(emailId: number): Promise<DraftEmail | undefined> 
    {
        return lastValueFrom
        (
            this._api.makeAPIDeleteCall<DraftEmail>(this._baseUrl,"/email/drafts/"+ emailId.toString(),{})
                
            .pipe
            (
                map
                (
                    e =>
                    {
                        this._store.dispatch(new Emails.EmailServiceEmailDraftDeleted({draftEmailId: emailId}));
                        this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Draft Email Deleted'});
                        return plainToInstance(DraftEmail, e);
                    }
                ),
                catchError((error:ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'errror', summary: 'Could Not Delete Draft Email', detail: ' ' + error.message }); return of(undefined)})
            )
        )
    }
}
